import React, { ChangeEvent, ReactElement, useState } from 'react';
import {
  Button,
  InsetText,
  LoaderButton,
  Table,
  Tag,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from 'idsk-react-components';
import { BackButton, ConfirmModal } from 'slovlex-idsk-react-components';
import './prechodZapisDoplnenehoStanoviska.scss';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useClient, useClientStatusContext } from '../../../context/ClientStatusContext';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import routes from '../../../routing/routes';
import downloadFile from '../../../function/downloadFile';
import getFileSize from '../../../function/getFileSize';
import Actions from '../../SprievodnyDokument/SprievodnyDokumentUpdateList/component/Actions';
import SprievodnyDokumentMergedItem from '../../../model/pracovnyKrok/SprievodnyDokumentMergedItem';
import {
  savePredbezneStanoviskoClient,
} from '../../../client/pracovnyKrok/predbezneStanoviskoClient';
import {
  useZapisDoplnenehoStanoviskaFormDataContext,
} from '../../../context/ZapisDoplnenehoStanoviska';

const PREDBEZNE_STANOVISKO_KOD = 'predbezneStanovisko';

const PrechodZapisDoplnenehoStanoviska = (): ReactElement => {
  const {
    type,
    year,
    index,
    pracovnyKrokId,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, pracovnyKrokId: string }>();

  const navigate = useNavigate();
  const { handleStatus, clearStatus } = useClientStatusContext();
  const { displaySuccess } = useSuccessMessageContext();

  const { generatedFile } = useZapisDoplnenehoStanoviskaFormDataContext();

  const typPredbezneStanovisko = {
    uuid: undefined,
    nazov: 'Predbežné stanovisko',
    kod: PREDBEZNE_STANOVISKO_KOD,
    povinny: true,
    platny: true,
    existujeFormular: false,
    povolitZadavanieFormularom: false,
    generovanySystemom: false,
    legislativny: false,
  };

  const [dokumentToBeUploaded, setDokumentToBeUploaded] = useState<SprievodnyDokumentMergedItem>({
    typDokumentu: typPredbezneStanovisko,
    sprievodnyDokumentUuid: generatedFile && '00000000-00000000-00000000-00000000',
    velkost: generatedFile?.size,
    povinny: true,
    nazov: generatedFile?.name,
  });

  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  const uploadDokument = (e: ChangeEvent<HTMLInputElement>): void => {
    clearStatus();
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setFileToUpload(file);
      setDokumentToBeUploaded({
        ...dokumentToBeUploaded,
        sprievodnyDokumentUuid: '00000000-00000000-00000000-00000000',
        nazov: file.name,
        velkost: file.size,
      });
      displaySuccess('Dokument bol úspešne pridaný.');
    }
  };

  const deleteDokument = (): void => {
    setFileToUpload(undefined);
    setDokumentToBeUploaded({ typDokumentu: typPredbezneStanovisko });
    displaySuccess('Dokument bol úspešne odstránený.');
  };

  const downloadDokument = (): void => {
    downloadFile(dokumentToBeUploaded?.nazov, fileToUpload);
  };

  const {
    wrapClient: savePredbezneStanovisko,
    status: predbezneStanoviskoStatus,
  } = useClient(savePredbezneStanoviskoClient);
  const uploadPredbezneStanovisko = async (): Promise<void> => {
    clearStatus();
    if (fileToUpload) {
      await savePredbezneStanovisko(type, year, index, pracovnyKrokId, fileToUpload);
      navigate(routes.procesy.detail.withParams(type, year, index));
      displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
    }
  };

  return (
    <div>
      <Table>
        <Thead>
          <Tr>
            <Th width="35%">Názov</Th>
            <Th width="20%">Typ dokumentu</Th>
            <Th width="10%">Veľkosť</Th>
            <Th width="35%" />
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td>{dokumentToBeUploaded?.nazov}</Td>
            <Td>
              <Tag size="medium">Povinné</Tag><br />
              {dokumentToBeUploaded?.typDokumentu?.nazov}
            </Td>
            <Td>
              {dokumentToBeUploaded?.velkost !== undefined
                && dokumentToBeUploaded?.velkost !== null
                && getFileSize(dokumentToBeUploaded.velkost)}
            </Td>
            <Td>
              <Actions
                sprievodnyDokument={dokumentToBeUploaded}
                uploadDokument={uploadDokument}
                uploadDokumentStatus={undefined}
                deleteDokument={deleteDokument}
                downloadDokument={downloadDokument}
              />
            </Td>
          </Tr>
        </Tbody>
      </Table>

      <InsetText type="warn-info">
        Tento dokument bude pridaný medzi sprievodné dokumenty a zároveň
        bude odoslaný v notifikácii NRSR.
      </InsetText>

      <div className="prechod-zapis-doplneneho-stanoviska-actions">
        {fileToUpload ? (
          <ConfirmModal
            question='Naozaj si prajete prejsť do štádia "Zápis doplneného predbežného stanoviska k návrhu aktu EÚ"?'
            description={'Po vykonaní tejto funkcionality bude uvedený dokument nahratý '
              + 'medzi sprievodné dokumenty, zároveň bude odoslaný v notifikácii NRSR a proces bude '
              + 'presunutý do štádia "Zápis doplneného predbežného stanoviska k návrhu aktu EÚ".'}
            cancelButtonText="Nie"
            confirmButton={(
              <Button buttonStyle="action" size="large">Áno</Button>
            )}
            openButton={(
              <LoaderButton
                className="prechod-zapis-doplneneho-stanoviska-actions__odoslat-notifikaciu"
                size="large"
                type="button"
                loading={predbezneStanoviskoStatus?.loading}
                disableOutsideClickWhileLoading
              >Prejsť do ďalšieho štádia
              </LoaderButton>
            )}
            doAfterConfirm={uploadPredbezneStanovisko}
          />
        ) : (
          <Button
            buttonStyle="action"
            size="large"
            className="prechod-zapis-doplneneho-stanoviska-actions__odoslat-notifikaciu"
            onClick={() => handleStatus({
              responseCode: 0,
              loading: false,
              finished: true,
              failed: true,
              generalErrors: [{ message: 'Akciu nie je možné vykonať. Nahrajte prosím požadovaný dokument.' }],
              fieldErrors: [],
            })}
          >
            Prejsť do nasledujúceho štádia
          </Button>
        )}
        <BackButton disabled={predbezneStanoviskoStatus?.loading} />
      </div>
    </div>
  );
};

export default PrechodZapisDoplnenehoStanoviska;
